import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/**
 * GlobalLoadingIndicator Component
 *
 * Shows a thin progress bar at the top of the page when React Query
 * operations are in progress (fetching or mutating data).
 *
 * Benefits:
 * - Improves perceived performance
 * - Provides visual feedback for background operations
 * - Non-intrusive (doesn't block UI)
 * - Respects prefers-reduced-motion for accessibility
 *
 * Usage:
 * Add once to the root layout or App component:
 * ```tsx
 * <GlobalLoadingIndicator />
 * ```
 */
export function GlobalLoadingIndicator() {
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  const [isVisible, setIsVisible] = useState(false);

  // Debounce visibility to avoid flickering for quick operations
  useEffect(() => {
    const isLoading = isFetching + isMutating > 0;

    if (isLoading) {
      // Show immediately when loading starts
      setIsVisible(true);
      return; // No cleanup needed when showing
    }

    // Delay hiding to avoid flicker
    const timeout = setTimeout(() => {
      setIsVisible(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [isFetching, isMutating]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 h-1"
      role="progressbar"
      aria-label="Loading"
      aria-live="polite"
    >
      {/* Animated progress bar */}
      <div
        className="h-full bg-sky-600 dark:bg-sky-500
                   animate-[loading_1.5s_ease-in-out_infinite]
                   motion-reduce:animate-none
                   motion-reduce:bg-sky-600/70 dark:motion-reduce:bg-sky-500/70"
        style={{
          width: '100%',
        }}
      />

      {/* Add keyframe animation in global styles if not present */}
      <style>
        {`
          @keyframes loading {
            0% {
              transform: translateX(-100%);
            }
            50% {
              transform: translateX(0%);
            }
            100% {
              transform: translateX(100%);
            }
          }
        `}
      </style>
    </div>
  );
}

/**
 * Simplified version with pulsing animation
 * Use this if you prefer a simpler, less distracting indicator
 */
export function GlobalLoadingIndicatorSimple() {
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  const isLoading = isFetching + isMutating > 0;

  if (!isLoading) {
    return null;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 h-1 bg-sky-600 dark:bg-sky-500
                 animate-pulse z-50 motion-reduce:animate-none"
      role="progressbar"
      aria-label="Loading"
      aria-live="polite"
    />
  );
}
